/**
 * Foreground agent slots + one UI lock.
 * Max 3 FG agents; only the lock-holder may run ui.* ops.
 */

import type { ZanaEpisode } from './zanaEpisodes';

export const FG_MAX = 3;

export type FgAgentStatus = 'queued' | 'working' | 'waiting_you' | 'done' | 'failed' | 'stopped';

export interface FgAgentSlot {
  agentId: string;
  name: string;
  topic: string;
  goal: string;
  status: FgAgentStatus;
  taskTitle: string;
  estimateMs?: number;
  startedAt: number;
  /** Walkthrough (teach) vs do-agent. */
  mode: 'walkthrough' | 'agent';
}

type Listener = () => void;
const listeners = new Set<Listener>();

let slots: FgAgentSlot[] = [];
let uiLockHolder: string | null = null;

function notify() {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

export function subscribeFgAgents(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function listFgAgents(): FgAgentSlot[] {
  return slots.slice();
}

export function getUiLockHolder(): string | null {
  return uiLockHolder;
}

export function tryAcquireUiLock(agentId: string): boolean {
  if (!uiLockHolder || uiLockHolder === agentId) {
    uiLockHolder = agentId;
    notify();
    return true;
  }
  return false;
}

export function releaseUiLock(agentId: string): void {
  if (uiLockHolder === agentId) {
    uiLockHolder = null;
    notify();
  }
}

/** Register a new FG slot. Returns null + reason if at capacity. */
export function registerFgAgent(
  slot: Omit<FgAgentSlot, 'status' | 'startedAt' | 'taskTitle'> & { taskTitle?: string },
): { ok: true; slot: FgAgentSlot } | { ok: false; reason: string } {
  const active = slots.filter((s) => s.status !== 'done' && s.status !== 'failed' && s.status !== 'stopped');
  if (active.length >= FG_MAX) {
    return {
      ok: false,
      reason: `I already have ${FG_MAX} mini-Zanas on the desk. Wait for one to finish, or stop one, before starting another.`,
    };
  }
  const next: FgAgentSlot = {
    ...slot,
    taskTitle: slot.taskTitle || 'Starting…',
    status: 'queued',
    startedAt: Date.now(),
  };
  slots = [...slots.filter((s) => s.agentId !== next.agentId), next];
  notify();
  return { ok: true, slot: next };
}

export function updateFgAgent(
  agentId: string,
  patch: Partial<Pick<FgAgentSlot, 'status' | 'taskTitle' | 'name'>>,
): void {
  slots = slots.map((s) => (s.agentId === agentId ? { ...s, ...patch } : s));
  notify();
}

export function releaseFgAgent(agentId: string): void {
  releaseUiLock(agentId);
  slots = slots.map((s) => (
    s.agentId === agentId && s.status !== 'done' && s.status !== 'failed'
      ? { ...s, status: 'stopped' as const }
      : s
  ));
  // Drop finished slots after a beat — keep list short
  const now = Date.now();
  slots = slots.filter((s) => {
    if (s.agentId === agentId) return false;
    if ((s.status === 'done' || s.status === 'failed' || s.status === 'stopped') && now - s.startedAt > 120_000) {
      return false;
    }
    return true;
  });
  notify();
}

export function markFgDone(agentId: string, _episode?: ZanaEpisode | null): void {
  updateFgAgent(agentId, { status: 'done', taskTitle: 'Done' });
  releaseUiLock(agentId);
}
